"use client"

import { useAtom } from "jotai"
import React, { useCallback } from "react"

import type { ParagraphAttributes } from "@/../types/GutenbergBlockAttributes"
import ParsedText from "@/components/course-material/ParsedText"
import dynamicImport from "@/shared-module/common/utils/dynamicImport"
import withErrorBoundary from "@/shared-module/common/utils/withErrorBoundary"
import { currentlyOpenFeedbackDialogAtom } from "@/stores/course-material/materialFeedbackStore"

import type { BlockRendererProps } from "../../.."
import { textAlignOf } from "../../../util/textAlign"
import EditingParagraph from "./proposing-edits/EditingParagraph"
import { getParagraphStyles } from "./styles"

const LatexParagraph = dynamicImport(() => import("./LatexParagraph"))

interface ExtraAttributes {
  backgroundColor?: string
  textColor?: string
}

const P = "p"

const ParagraphBlock: React.FC<
  React.PropsWithChildren<BlockRendererProps<ParagraphAttributes & ExtraAttributes>>
> = ({ data, id }) => {
  const { textColor, backgroundColor, fontSize, content, dropCap, fitText, style } = data.attributes
  const align = textAlignOf(style, data.attributes.align)
  const [type] = useAtom(currentlyOpenFeedbackDialogAtom)
  const isEditing = type === "proposed-edits"

  const renderFunction = useCallback(
    ({
      ref,
      count,
      hasCitationsOrGlossary,
    }: {
      ref: (node: HTMLElement | null) => void
      count: number
      hasCitationsOrGlossary: boolean
    }) => {
      const ParagraphComponent = count > 0 ? LatexParagraph : P
      const hideOverflow = !hasCitationsOrGlossary
      return (
        <ParagraphComponent
          ref={ref}
          className={getParagraphStyles(
            textColor,
            backgroundColor,
            fontSize,
            hideOverflow,
            dropCap,
            align,
            fitText,
          )}
        />
      )
    },
    [textColor, backgroundColor, fontSize, dropCap, align, fitText],
  )

  if (isEditing) {
    return <EditingParagraph data={data} id={id} />
  }

  return <ParsedText text={content} render={renderFunction} useWrapperElement={true} />
}

const exported = withErrorBoundary(ParagraphBlock)
// @ts-expect-error: Custom property
exported.dontUseDefaultBlockMargin = true

export default exported
